import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Plus, Tags, Eye, Download, Upload, Pencil, Archive, ArchiveRestore, Trash2, AlertTriangle, CheckCircle2, Clock, History } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import usePotvrzeni from '../ui/usePotvrzeni';
import SmernicPreviewModal from '../SmernicPreviewModal';
import { muze } from '../../lib/moduly';
import NastaveniModulu from '../layout/NastaveniModulu';
import { KategorieSmernic } from '../nastaveni/Ciselniky';
import { BARVA, BARVA_BG, api, datum, barva as barvaZnalosti } from './znalosti';
import { CilPrirazeni, StitekPrirazeni, NovaSmerniceModal, UpravitSmerniciModal, NovaVerzeModal, nactiCiselnikyCilu } from './SmerniceModaly';

/**
 * Znalosti → **Směrnice** — správa směrnic pro vedení (od 2026-10-11, dřív
 * samostatný modul `bezpecnost` se záložkami Přehled a Správa).
 *
 * Zaměstnanci směrnice čtou a potvrzují ve Znalostech ze spodní lišty; tady
 * je všechno, co k nim potřebuje správce, na jedné obrazovce (norma §1):
 *
 *  - **seznam** podle kategorií, pohledy Aktivní · Čeká na potvrzení ·
 *    Bez přiřazení · Archiv; v přehledu sloupce přiřazení, verze a potvrzení
 *  - **detail** ve sloupci: soubor, kdo potvrdil a kdo ne, komu patří, verze
 *  - **třetí sloupec** (`panelVzdy`) — přehled potvrzení napříč směrnicemi
 *    a „K dořešení"; na mobilu řádek K dořešení nad seznamem (§1g)
 *  - nová směrnice, úprava údajů a nová verze jsou modaly (rychlá akce)
 *
 * API zůstává `/api/bezpecnost/smernice/*`, právo je `znalosti.smernice`.
 */

const POHLEDY = [
    { id: 'aktivni', label: 'Aktivní', patri: (s) => s.stav === 'aktivni' },
    { id: 'ceka', label: 'Čeká na potvrzení', patri: (s, st) => s.stav === 'aktivni' && s.vyzaduje_podpis && !!st[s.id] && st[s.id].podepsano < st[s.id].celkem },
    { id: 'bez', label: 'Bez přiřazení', upozorneni: true, patri: (s) => s.stav === 'aktivni' && !s.prirazeni_count },
    { id: 'archiv', label: 'Archiv', patri: (s) => s.stav === 'archivovano' },
];

const sklon = (n, [jedna, dve, pet]) => (n === 1 ? jedna : n >= 2 && n <= 4 ? dve : pet);
const verzeSmernice = (s) => s.aktualni_verze || s.verze?.[0] || null;

/**
 * Co u směrnic nehraje — jeden zdroj pro třetí sloupec i mobilní modal (§1g).
 * Bez přiřazení = problém (směrnici nikdo nevidí), nepotvrzená = upozornění.
 */
export function kDoreseniSmernic(smernice, stats) {
    const aktivni = smernice.filter(s => s.stav === 'aktivni');
    return [
        ...aktivni.filter(s => !s.prirazeni_count).map(s => ({
            id: `bez-${s.id}`, smerniceId: s.id, problem: true, titulek: s.nazev, popis: 'Bez přiřazení — nikdo ji nevidí',
        })),
        ...aktivni.filter(s => s.prirazeni_count && s.vyzaduje_podpis && stats[s.id] && stats[s.id].podepsano < stats[s.id].celkem)
            .map(s => ({ s, chybi: stats[s.id].celkem - stats[s.id].podepsano }))
            .sort((a, b) => b.chybi - a.chybi)
            .map(({ s, chybi }) => ({
                id: `pod-${s.id}`, smerniceId: s.id, problem: false, titulek: s.nazev,
                popis: `Potvrdilo ${stats[s.id].podepsano} z ${stats[s.id].celkem}, chybí ${chybi}`,
            })),
    ];
}

export default function SmerniceSprava({ user, isDark, isMobile, cardStyle, inputStyle, muted, cara, rezervaDole, vybranaZvenku = null }) {
    const akcent = barvaZnalosti(isDark);
    const upravuje = muze(user, 'znalosti.smernice', 'upravuje');

    const [smernice, setSmernice] = useState(null);
    const [kategorie, setKategorie] = useState([]);
    const [stats, setStats] = useState({});           // { smernice_id: { celkem, podepsano } }
    const [tym, setTym] = useState([]);               // [{ jmeno, prijmeni, nepodepsane: [] }]
    const [hledani, setHledani] = useState('');
    const [pohled, setPohled] = useState('aktivni');
    const [vybranyId, setVybranyId] = useState(null);
    const [obnova, setObnova] = useState(0);          // detail si po změně přenačte podpisy a přiřazení
    const [modal, setModal] = useState(null);         // { druh: 'nova' | 'upravit' | 'verze' | 'kategorie', smernice? }
    const [nahled, setNahled] = useState(null);       // { smernice, verze }
    const [potvrdit, potvrzeniDialog] = usePotvrzeni(isDark);

    const nacti = useCallback(() => Promise.all([
        api('/api/bezpecnost/smernice').catch(() => []),
        api('/api/ciselniky/smernice-kategorie').catch(() => []),
        api('/api/bezpecnost/smernice/podrizeni-stats').catch(() => []),
        api('/api/bezpecnost/smernice/nepodepsane-tym?ids=all').catch(() => []),
    ]).then(([s, k, st, t]) => {
        setSmernice(Array.isArray(s) ? s : []);
        setKategorie(Array.isArray(k) ? k : []);
        setStats(Object.fromEntries((Array.isArray(st) ? st : []).map(x => [x.smernice_id, x])));
        setTym(Array.isArray(t) ? t : []);
    }), []);
    useEffect(() => { nacti(); }, [nacti]);
    const poZmene = () => { setObnova(o => o + 1); return nacti(); };

    useEffect(() => { if (vybranaZvenku) setVybranyId(Number(vybranaZvenku)); }, [vybranaZvenku]);

    const vsechny = useMemo(() => smernice || [], [smernice]);
    const kDoreseni = useMemo(() => kDoreseniSmernic(vsechny, stats), [vsechny, stats]);
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, vsechny.filter(s => p.patri(s, stats)).length])), [vsechny, stats]);
    const akt = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const q = hledani.trim().toLowerCase();
    const seznam = vsechny.filter(s => akt.patri(s, stats)
        && (!q || `${s.nazev} ${s.cislo_dokumentu || ''} ${s.kategorie?.nazev || ''} ${s.popis || ''}`.toLowerCase().includes(q)));
    const skupiny = new Map();
    seznam.forEach(s => {
        const k = s.kategorie?.nazev || 'Ostatní';
        if (!skupiny.has(k)) skupiny.set(k, []);
        skupiny.get(k).push(s);
    });
    const vybrana = vsechny.find(s => s.id === vybranyId) || null;

    const otevriNahled = (s, verze = verzeSmernice(s)) => { if (verze) setNahled({ smernice: s, verze }); };

    // ── Seznam ──
    const potvrzeni = (s) => {
        if (!s.vyzaduje_podpis) return { text: 'bez potvrzení', podil: null };
        const st = stats[s.id];
        if (!st) return { text: '—', podil: null };
        return { text: `${st.podepsano}/${st.celkem}`, podil: st.celkem ? st.podepsano / st.celkem : 1 };
    };

    const radek = (s, siroky) => {
        const sel = s.id === vybranyId;
        const av = verzeSmernice(s);
        const pot = potvrzeni(s);
        const bez = s.stav === 'aktivni' && !s.prirazeni_count;
        const meta = [s.cislo_dokumentu, av ? `v${av.cislo_verze}` : null, siroky ? null : (bez ? null : pot.podil !== null ? `potvrdilo ${pot.text}` : pot.text === 'bez potvrzení' ? pot.text : null)].filter(Boolean).join(' · ');
        return (
            <button key={s.id} type="button" onClick={() => setVybranyId(s.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 58, cursor: 'pointer', border: 'none', borderBottom: `1px solid ${cara}`,
                    background: sel ? BARVA_BG : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${BARVA}` : 'none', opacity: s.stav === 'archivovano' ? 0.6 : 1 }}>
                <span className="grid place-items-center" style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: BARVA_BG, color: akcent }}>
                    <ShieldCheck size={17} />
                </span>
                <span className="flex-1 min-w-0">
                    <span className="block truncate font-semibold" style={{ color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{s.nazev}</span>
                    <span className="flex items-center" style={{ gap: 6, marginTop: 2, fontSize: isMobile ? 13 : 12, color: muted, minWidth: 0 }}>
                        {bez && !siroky && <span style={{ color: '#EF4444', fontWeight: 700, flexShrink: 0 }}>Bez přiřazení</span>}
                        <span className="truncate" style={{ fontVariantNumeric: 'tabular-nums' }}>{meta}</span>
                    </span>
                </span>
                {siroky && (
                    <span style={{ width: 110, flexShrink: 0, textAlign: 'right', fontSize: 12.5, fontWeight: bez ? 700 : 500, color: bez ? '#EF4444' : 'var(--text-primary)' }}>
                        {s.stav === 'archivovano' ? '—' : bez ? 'nikomu' : `${s.prirazeni_count} ${sklon(s.prirazeni_count, ['cíl', 'cíle', 'cílů'])}`}
                    </span>
                )}
                {siroky && (
                    <span className="flex items-center justify-end" style={{ width: 150, flexShrink: 0, gap: 8, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: pot.podil === null ? muted : 'var(--text-primary)' }}>
                        {pot.podil !== null && (
                            <span aria-hidden="true" style={{ width: 56, height: 5, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                                <span style={{ display: 'block', height: '100%', width: `${Math.round(pot.podil * 100)}%`, background: akcent }} />
                            </span>
                        )}
                        {pot.text}
                    </span>
                )}
                {siroky && <span style={{ width: 96, flexShrink: 0, textAlign: 'right', fontSize: 12, color: muted }}>{datum(av?.created_at || s.created_at)}</span>}
                {!siroky && bez && <AlertTriangle size={15} style={{ color: '#EF4444', flexShrink: 0 }} aria-label="K dořešení" />}
            </button>
        );
    };

    const nadpisSkupiny = (t, n) => (
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: muted, margin: 0, padding: '14px 14px 6px' }}>{t} · {n}</p>
    );

    const otevriZDoreseni = (k) => { setPohled('aktivni'); setVybranyId(k.smerniceId); };
    const akceDoreseni = (k) => (
        <button type="button" onClick={() => otevriZDoreseni(k)} style={{ fontSize: 12, fontWeight: 600, color: akcent, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
            Otevřít směrnici
        </button>
    );

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={BARVA}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat směrnici nebo číslo…', placeholderKratky: 'Hledat' }}
                akce={upravuje ? [
                    { id: 'nova', label: 'Směrnice', Icon: Plus, primarni: true, onClick: () => setModal({ druh: 'nova' }) },
                    // Kategorie (dřív Nastavení → Typy směrnic) — norma §2: kategorie se spravují v hlavičce seznamu
                    ...(isMobile ? [] : [{ id: 'kategorie', label: 'Kategorie', Icon: Tags, onClick: () => setModal({ druh: 'kategorie' }) }]),
                ] : []}
                pohled={POHLEDY.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={akt.id === p.id} upozorneni={p.upozorneni && pocty[p.id] > 0}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={BARVA} kompaktni={isMobile} />
                ))}
                pod={isMobile && kDoreseni.length > 0 ? (
                    <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={akcent} styl={{ marginTop: 8 }} renderAkce={akceDoreseni}
                        navic={<div style={{ padding: '4px 16px 8px' }}><PrehledCisla smernice={vsechny} stats={stats} tym={tym} muted={muted} cara={cara} /></div>} />
                ) : null} />
            {siroky && (
                <div className="flex items-center" style={{ gap: 12, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}` }}>
                    <span style={{ width: 36 }} />
                    <span className="flex-1">Směrnice</span>
                    <span style={{ width: 110, textAlign: 'right' }}>Přiřazení</span>
                    <span style={{ width: 150, textAlign: 'right' }}>Potvrdilo</span>
                    <span style={{ width: 96, textAlign: 'right' }}>Verze z</span>
                </div>
            )}
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {smernice === null && <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>Načítám…</p>}
                {smernice && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {vsechny.length === 0 ? 'Zatím žádné směrnice.' : q ? 'Nic se neshoduje.' : 'V tomhle pohledu nic není.'}
                    </p>
                )}
                {[...skupiny.entries()].map(([nazev, polozky]) => (
                    <div key={nazev}>
                        {nadpisSkupiny(nazev, polozky.length)}
                        {polozky.map(s => radek(s, siroky))}
                    </div>
                ))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    // ── Akce nad vybranou ──
    const archivovat = async (s) => {
        const doArchivu = s.stav === 'aktivni';
        if (doArchivu && !(await potvrdit('Archivovat směrnici?', { text: 'Lidé ji přestanou vidět i potvrzovat. Podpisy zůstanou v evidenci.', potvrdit: 'Archivovat', nebezpecne: false }))) return;
        await api(`/api/bezpecnost/smernice/${s.id}`, { method: 'PUT', body: { stav: doArchivu ? 'archivovano' : 'aktivni' } }).catch(() => {});
        poZmene();
    };
    const smazat = async (s) => {
        if (!(await potvrdit('Smazat směrnici?', { text: 'Smaže se se všemi verzemi i záznamy o tom, kdo ji potvrdil. Pro evidenci je bezpečnější ji archivovat.', potvrdit: 'Smazat', nebezpecne: true }))) return;
        await api(`/api/bezpecnost/smernice/${s.id}`, { method: 'DELETE' }).catch(() => {});
        setVybranyId(null);
        poZmene();
    };

    const detailPanel = () => vybrana ? (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                <SmerniceDetail key={vybrana.id} s={vybrana} obnova={obnova} upravuje={upravuje} isDark={isDark} isMobile={isMobile} muted={muted} cara={cara}
                    onZpet={() => setVybranyId(null)} onNahled={otevriNahled}
                    onUpravit={() => setModal({ druh: 'upravit', smernice: vybrana })}
                    onNovaVerze={() => setModal({ druh: 'verze', smernice: vybrana })}
                    onArchivovat={() => archivovat(vybrana)} onSmazat={() => smazat(vybrana)}
                    onZmenaPrirazeni={nacti} />
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    ) : null;

    const panel = () => (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, overflowY: 'auto', minHeight: 0 }}>
            <PrehledCisla smernice={vsechny} stats={stats} tym={tym} muted={muted} cara={cara} />
            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' }}>K dořešení</p>
            <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne="Všechny směrnice mají komu patřit a všichni je potvrdili." renderAkce={akceDoreseni} />
        </div>
    );

    return (
        <>
            <ModulLayout isMobile={isMobile} vybrano={!!vybrana} mobilRezim mobilPanel={false} panelVzdy barva={BARVA} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />

            {modal?.druh === 'kategorie' && (
                <NastaveniModulu nadpis="Kategorie směrnic" barva={BARVA} isDark={isDark} onClose={() => { setModal(null); nacti(); }}
                    sekce={[{ id: 'kategorie', nazev: 'Kategorie', obsah: () => <KategorieSmernic isDark={isDark} barva={BARVA} /> }]} />
            )}
            {modal?.druh === 'nova' && (
                <NovaSmerniceModal isDark={isDark} kategorie={kategorie} onClose={() => setModal(null)}
                    onUlozeno={(nova) => { setModal(null); setPohled('aktivni'); poZmene().then(() => setVybranyId(nova.id)); }} />
            )}
            {modal?.druh === 'upravit' && (
                <UpravitSmerniciModal isDark={isDark} smernice={modal.smernice} kategorie={kategorie} onClose={() => setModal(null)}
                    onUlozeno={() => { setModal(null); poZmene(); }} />
            )}
            {modal?.druh === 'verze' && (
                <NovaVerzeModal isDark={isDark} smernice={modal.smernice} onClose={() => setModal(null)}
                    onUlozeno={() => { setModal(null); poZmene(); }} />
            )}
            {nahled && (
                <SmernicPreviewModal smernice={nahled.smernice} verze={nahled.verze} user={user} uzPodepsano={nahled.smernice.muj_podpis}
                    onClose={() => setNahled(null)}
                    onPotvrdit={async (id) => {
                        await api(`/api/bezpecnost/smernice/${id}/potvrdit`, { method: 'POST' }).catch(() => {});
                        setNahled(n => (n ? { ...n, smernice: { ...n.smernice, muj_podpis: true } } : n));
                        poZmene();
                    }} />
            )}
            {potvrzeniDialog}
        </>
    );
}

/** Přehled potvrzení napříč směrnicemi — třetí sloupec a mobilní modal K dořešení. */
function PrehledCisla({ smernice, stats, tym, muted, cara }) {
    const aktivni = smernice.filter(s => s.stav === 'aktivni');
    const sPodpisem = aktivni.filter(s => s.vyzaduje_podpis);
    const celkem = sPodpisem.reduce((n, s) => n + (stats[s.id]?.celkem || 0), 0);
    const podepsano = sPodpisem.reduce((n, s) => n + (stats[s.id]?.podepsano || 0), 0);
    const dluzi = tym.filter(z => z.nepodepsane?.length).sort((a, b) => b.nepodepsane.length - a.nepodepsane.length);
    const r = (l, h, cervene = false) => (
        <div key={l} className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}`, gap: 10 }}>
            <span className="truncate" style={{ color: muted }}>{l}</span>
            <b style={{ color: cervene ? '#EF4444' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{h}</b>
        </div>
    );
    const nadpis = (t, mt = 0) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: `${mt}px 0 6px` }}>{t}</p>;
    return (
        <>
            {nadpis('Směrnice')}
            {r('Aktivních', aktivni.length)}
            {r('Vyžaduje potvrzení', sPodpisem.length)}
            {r('Bez přiřazení', aktivni.filter(s => !s.prirazeni_count).length, aktivni.some(s => !s.prirazeni_count))}
            {r('V archivu', smernice.length - aktivni.length)}
            {nadpis('Potvrzení', 16)}
            {r('Splněno', celkem ? `${Math.round((podepsano / celkem) * 100)} %` : '—')}
            {r('Potvrzeno', `${podepsano} z ${celkem}`)}
            {r('Lidé s nepotvrzenou', dluzi.length)}
            {dluzi.length > 0 && (
                <>
                    {nadpis('Nejvíc chybí', 16)}
                    {dluzi.slice(0, 5).map(z => r(`${z.jmeno} ${z.prijmeni}`, z.nepodepsane.length))}
                    {dluzi.length > 5 && <p style={{ margin: '6px 0 0', fontSize: 12, color: muted }}>a {dluzi.length - 5} dalších</p>}
                </>
            )}
        </>
    );
}

/** Detail směrnice ve sloupci: soubor, potvrzení, komu patří, verze. */
function SmerniceDetail({ s, obnova, upravuje, isDark, isMobile, muted, cara, onZpet, onNahled, onUpravit, onNovaVerze, onArchivovat, onSmazat, onZmenaPrirazeni }) {
    const akcent = barvaZnalosti(isDark);
    const av = verzeSmernice(s);
    const archiv = s.stav === 'archivovano';
    const [podpisy, setPodpisy] = useState(null);
    const [prirazeni, setPrirazeni] = useState(null);
    const [ciselniky, setCiselniky] = useState(null);
    const [chyba, setChyba] = useState(null);

    useEffect(() => {
        setPodpisy(null); setPrirazeni(null);
        api(`/api/bezpecnost/smernice/${s.id}/podpisy`).then(setPodpisy).catch(() => setPodpisy({ podepsani: [], nepodepsani: [] }));
        api(`/api/bezpecnost/smernice/${s.id}/prirazeni`).then(setPrirazeni).catch(() => setPrirazeni([]));
    }, [s.id, av?.id, obnova]);
    useEffect(() => { if (upravuje && !archiv) nactiCiselnikyCilu().then(setCiselniky); }, [upravuje, archiv]);

    const pridat = async (p) => {
        setChyba(null);
        try {
            const novy = await api(`/api/bezpecnost/smernice/${s.id}/prirazeni`, { method: 'POST', body: { typ_cile: p.typ_cile, cil_id: p.cil_id } });
            setPrirazeni(x => [...(x || []).filter(y => y.id !== novy.id), novy]);
            api(`/api/bezpecnost/smernice/${s.id}/podpisy`).then(setPodpisy).catch(() => {});
            onZmenaPrirazeni();
        } catch (e) { setChyba(e.message); }
    };
    const odebrat = async (p) => {
        setChyba(null);
        try {
            await api(`/api/bezpecnost/smernice/${s.id}/prirazeni/${p.id}`, { method: 'DELETE' });
            setPrirazeni(x => (x || []).filter(y => y.id !== p.id));
            api(`/api/bezpecnost/smernice/${s.id}/podpisy`).then(setPodpisy).catch(() => {});
            onZmenaPrirazeni();
        } catch (e) { setChyba(e.message); }
    };

    const tlac = (Icon, label, onClick, { nebezpecne = false, jenIkona = false } = {}) => (
        <button type="button" onClick={onClick} aria-label={label} title={label} className="inline-flex items-center gap-1.5 rounded-lg"
            style={{ height: isMobile ? 40 : 34, padding: jenIkona ? '0 10px' : '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent',
                color: nebezpecne ? '#EF4444' : 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
            <Icon size={14} />{!jenIkona && ` ${label}`}
        </button>
    );
    const sekce = (titulek, pravo = null) => (
        <div className="flex items-center justify-between" style={{ margin: '22px 0 8px', gap: 8 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted }}>{titulek}</p>
            {pravo}
        </div>
    );
    const clovek = (p, popis, barvaIkony, Ikona) => (
        <div key={p.zamestnanec_id} className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 44 : 34, borderBottom: `1px solid ${cara}`, fontSize: 13.5 }}>
            <Ikona size={14} style={{ color: barvaIkony, flexShrink: 0 }} />
            <span className="flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{p.jmeno} {p.prijmeni}</span>
            {popis && <span style={{ fontSize: 12, color: muted, flexShrink: 0 }}>{popis}</span>}
        </div>
    );

    const nepodepsani = podpisy?.nepodepsani || [];
    const podepsani = podpisy?.podepsani || [];
    const celkem = nepodepsani.length + podepsani.length;

    return (
        <div style={{ padding: isMobile ? '6px 16px 16px' : '18px 20px 20px' }}>
            {isMobile && <ZpetTlacitko onClick={onZpet} popis="Zpět na směrnice" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
            <p className="flex items-center" style={{ gap: 6, margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: akcent }}>
                <ShieldCheck size={13} /> {[s.kategorie?.nazev || 'Směrnice', s.cislo_dokumentu].filter(Boolean).join(' · ')}
            </p>
            <h1 style={{ margin: '6px 0 6px', fontSize: isMobile ? 22 : 20, fontWeight: 750, letterSpacing: '-0.3px', lineHeight: 1.2, color: 'var(--text-primary)' }}>{s.nazev}</h1>
            <p className="flex flex-wrap items-center" style={{ gap: 6, margin: 0, fontSize: 12.5, color: muted }}>
                {av && <span>verze {av.cislo_verze} · {datum(av.created_at)}</span>}
                {(av?.nahral || s.nahral) && <span>· {(av?.nahral || s.nahral).jmeno} {(av?.nahral || s.nahral).prijmeni}</span>}
                {!s.vyzaduje_podpis && <span>· bez potvrzení</span>}
                {archiv && <span style={{ padding: '1px 8px', borderRadius: 999, background: 'rgba(127,127,140,0.15)', fontWeight: 700, fontSize: 11 }}>Archiv</span>}
            </p>
            {s.popis && <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--text-primary)' }}>{s.popis}</p>}

            <div className="flex flex-wrap" style={{ gap: 6, marginTop: 14 }}>
                {av && tlac(Eye, 'Zobrazit', () => onNahled(s))}
                {av && (
                    <a href={`/api/bezpecnost/smernice/${s.id}/download`} className="inline-flex items-center gap-1.5 rounded-lg"
                        style={{ height: isMobile ? 40 : 34, padding: '0 12px', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none', border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                        <Download size={14} /> Stáhnout
                    </a>
                )}
                {upravuje && !archiv && tlac(Upload, 'Nová verze', onNovaVerze)}
                {upravuje && tlac(Pencil, 'Upravit', onUpravit)}
                {upravuje && tlac(archiv ? ArchiveRestore : Archive, archiv ? 'Obnovit' : 'Archivovat', onArchivovat)}
                {upravuje && tlac(Trash2, 'Smazat', onSmazat, { nebezpecne: true, jenIkona: true })}
            </div>

            {/* Kdo potvrdil — jen u aktivní směrnice, která potvrzení vyžaduje */}
            {s.vyzaduje_podpis && !archiv && (
                <>
                    {sekce(av ? `Potvrzení verze ${av.cislo_verze}` : 'Potvrzení',
                        podpisy && celkem > 0 ? <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{podepsani.length} z {celkem}</span> : null)}
                    {podpisy === null && <p className="text-sm" style={{ color: muted }}>Načítám…</p>}
                    {podpisy && celkem === 0 && <p className="text-sm" style={{ color: muted }}>Směrnice zatím nikomu nepatří, není kdo by ji potvrdil.</p>}
                    {podpisy && celkem > 0 && (
                        <>
                            <div aria-hidden="true" style={{ height: 6, borderRadius: 3, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)', marginBottom: 6 }}>
                                <div style={{ height: '100%', width: `${Math.round((podepsani.length / celkem) * 100)}%`, background: akcent }} />
                            </div>
                            {nepodepsani.map(p => clovek(p, 'čeká', '#F59E0B', Clock))}
                            {podepsani.map(p => clovek(p, datum(p.potvrzeno_at), akcent, CheckCircle2))}
                        </>
                    )}
                </>
            )}

            {sekce('Komu patří')}
            {prirazeni === null && <p className="text-sm" style={{ color: muted }}>Načítám…</p>}
            {prirazeni && prirazeni.length === 0 && (
                <p className="flex items-center" style={{ gap: 6, margin: '0 0 8px', fontSize: 13, color: '#EF4444', fontWeight: 600 }}>
                    <AlertTriangle size={14} /> Bez přiřazení — směrnici nikdo nevidí.
                </p>
            )}
            {prirazeni && prirazeni.length > 0 && (
                <div className="flex flex-wrap" style={{ gap: 6, marginBottom: 10 }}>
                    {prirazeni.map(p => <StitekPrirazeni key={p.id} p={p} isDark={isDark} onOdebrat={upravuje && !archiv ? () => odebrat(p) : null} />)}
                </div>
            )}
            {upravuje && !archiv && <CilPrirazeni ciselniky={ciselniky} isDark={isDark} kompaktni={isMobile} onPridat={pridat} />}
            {chyba && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#EF4444' }}>{chyba}</p>}

            {sekce('Verze', <History size={14} style={{ color: muted }} />)}
            {(s.verze || []).map(v => (
                <div key={v.id} className="flex items-center" style={{ gap: 10, minHeight: isMobile ? 52 : 42, borderBottom: `1px solid ${cara}` }}>
                    <span className="tabular-nums" style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 6, background: BARVA_BG, color: akcent }}>v{v.cislo_verze}</span>
                    <span className="flex-1 min-w-0">
                        <span className="block truncate" style={{ fontSize: 13, color: 'var(--text-primary)' }}>{v.poznamka || v.soubor_nazev}</span>
                        <span className="block truncate" style={{ fontSize: 11.5, color: muted }}>
                            {[datum(v.created_at), v.nahral ? `${v.nahral.jmeno} ${v.nahral.prijmeni}` : null, v.poznamka ? v.soubor_nazev : null].filter(Boolean).join(' · ')}
                        </span>
                    </span>
                    <button type="button" onClick={() => onNahled(s, v)} aria-label={`Zobrazit verzi ${v.cislo_verze}`}
                        style={{ width: 34, height: 34, display: 'grid', placeItems: 'center', border: 'none', background: 'none', color: muted, cursor: 'pointer' }}><Eye size={15} /></button>
                    <a href={`/api/bezpecnost/smernice/${s.id}/download?verze_id=${v.id}`} aria-label={`Stáhnout verzi ${v.cislo_verze}`}
                        style={{ width: 34, height: 34, display: 'grid', placeItems: 'center', color: muted }}><Download size={15} /></a>
                </div>
            ))}
        </div>
    );
}
